import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const spamValue = (formData.get('website_alt') as string || '').trim();
    if (spamValue) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d96dusf0d76aer3t53ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setErrorMsg(serverMsg || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please check your connection.');
      setStatus('error');
    }
  };

  const footerLinks = [
    {
      title: 'Quick Links',
      links: [
        { label: 'CSSBuy Spreadsheet Guide', to: '/cssbuy-spreadsheet-complete-guide/' },
        { label: 'Template Collection', to: '/cssbuy-spreadsheet-template-guide/' },
        { label: 'Shopping Guide', to: '/cssbuy-spreadsheet-shopping-guide/' },
        { label: 'Competitor Comparison', to: '/cssbuy-spreadsheet-vs-competitors/' },
        { label: 'Safety & Security', to: '/cssbuy-spreadsheet-safety/' },
      ],
    },
    {
      title: 'Product Categories',
      links: [
        { label: 'Shoes', href: 'https://kaspreadsheet.com/category-911-Shoes.html' },
        { label: 'Hoodies & Sweaters', href: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
        { label: 'T-Shirts', href: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
        { label: 'Jackets', href: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
      ],
    },
    {
      title: 'Follow Us',
      links: [
        { label: 'Instagram', href: '#', icon: 'ri-instagram-line' },
        { label: 'YouTube', href: '#', icon: 'ri-youtube-line' },
        { label: 'Facebook', href: '#', icon: 'ri-facebook-line' },
      ],
    },
  ];

  return (
    <footer className="bg-background-950 rounded-t-2xl mt-4 mx-3 md:mx-6">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="text-white text-xl font-heading font-bold">
              CSSBuy Spreadsheet
            </Link>
            <p className="text-foreground-400 text-sm mt-4 leading-relaxed max-w-xs">
              The ultimate resource hub for CSSBuy shopping spreadsheets. Free templates,
              expert guides, and comparison tools.
            </p>
          </div>

          {/* Link columns */}
          {footerLinks.map((col) => (
            <div key={col.title}>
              <h4 className="text-white text-sm font-semibold mb-4">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        className="text-foreground-400 hover:text-white text-sm transition-colors duration-150 flex items-center gap-2"
                      >
                        {link.icon && (
                          <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                        )}
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-foreground-400 hover:text-white text-sm transition-colors duration-150 flex items-center gap-2"
                      >
                        {link.icon && (
                          <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                        )}
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-10 border-t border-background-800">
          <div className="max-w-md">
            <p className="text-white text-sm font-semibold mb-1">
              Stay Updated
            </p>
            <p className="text-foreground-400 text-xs mb-4">
              Get the latest CSSBuy spreadsheet tips and template updates.
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex gap-2"
            >
              <div className="spam-guard">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 bg-background-800 border border-background-700 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                className="w-10 h-10 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-line text-white"></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="text-primary-400 text-xs mt-2">Thanks for subscribing! Check your inbox.</p>
            )}
            {status === 'error' && (
              <p className="text-red-400 text-xs mt-2">{errorMsg}</p>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-background-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-foreground-500 text-xs">
            &copy; {new Date().getFullYear()} CSSBuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-foreground-500 hover:text-foreground-300 text-xs transition-colors">Privacy Policy</a>
            <a href="#" className="text-foreground-500 hover:text-foreground-300 text-xs transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>

      {/* Background decoration */}
      <div className="overflow-hidden pb-6 md:pb-8">
        <p className="text-background-800 font-heading font-bold text-6xl md:text-8xl text-center whitespace-nowrap select-none">
          CSSBuy Spreadsheet
        </p>
      </div>
    </footer>
  );
}